import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../hooks/useAuth';

interface UnreadComment {
  id: string;
  story_id: string;
  story_title: string;
  content: string;
  created_at: string;
  commenter_name: string;
  commenter_id: string;
}

interface ProfileHeaderProps {
  profile: {
    displayName: string;
    username?: string;
    bio: string;
    avatarUrl: string;
    joinDate: string | null;
    role: string;
  };
  onEditClick: () => void;
  onAvatarUpdate?: (url: string) => void;
  onUsernameUpdate?: (username: string) => void;
  unreadCount?: number;
  unreadComments?: UnreadComment[];
  onMarkAsRead?: () => void;
}

export default function ProfileHeader({
  profile,
  onAvatarUpdate,
  onUsernameUpdate,
  unreadCount = 0,
  unreadComments = [],
  onMarkAsRead,
}: ProfileHeaderProps) {
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [displayedComments, setDisplayedComments] = useState<UnreadComment[]>([]);
  const notifRef = useRef<HTMLDivElement>(null);

  // Avatar upload states
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [cropScale, setCropScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Username edit states
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(profile.displayName || '');
  const [nameError, setNameError] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    setNameValue(profile.displayName || '');
  }, [profile.displayName]);

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatTimestamp = (dateString: string) => {
    const diffMs = Date.now() - new Date(dateString).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleNotificationClick = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    if (next) {
      // Snapshot the current comments so they stay visible after markAsRead clears them
      setDisplayedComments(unreadComments);
      if (unreadCount > 0 && onMarkAsRead) {
        onMarkAsRead();
      }
    }
  };

  // --- Avatar upload ---
  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('Image must be less than 5MB'); return; }
    setUploadError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setSelectedImage(ev.target?.result as string);
      setShowCropper(true);
      setCropPosition({ x: 0, y: 0 });
      setCropScale(1);
    };
    reader.readAsDataURL(file);
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !selectedImage) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const size = 300;
    canvas.width = size;
    canvas.height = size;
    ctx.fillStyle = '#18181b';
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(image, cropPosition.x, cropPosition.y, image.naturalWidth * cropScale, image.naturalHeight * cropScale);
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, size, size);
  };

  useEffect(() => {
    if (showCropper && selectedImage) drawCanvas();
  }, [showCropper, selectedImage, cropPosition, cropScale]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - cropPosition.x, y: e.clientY - cropPosition.y });
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    setCropPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setIsDragging(false);

  const handleCropConfirm = async () => {
    if (!canvasRef.current || !user) return;
    setIsUploading(true);
    try {
      const blob = await new Promise<Blob>((resolve) => canvasRef.current!.toBlob((b) => resolve(b!), 'image/jpeg', 0.9));
      const fileName = `${user.id}/avatar.jpg`;
      const { error: uploadError } = await supabase.storage.from('avatars').upload(fileName, blob, { upsert: true, contentType: 'image/jpeg' });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(fileName);
      onAvatarUpdate?.(publicUrl);
      setShowCropper(false);
      setSelectedImage(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      setUploadError('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // --- Display Name edit ---
  const validateName = (val: string) => {
    if (!val.trim()) return 'Name is required';
    if (val.length < 2) return 'At least 2 characters';
    if (val.length > 50) return 'Max 50 characters';
    return '';
  };

  const handleNameSave = async () => {
    const err = validateName(nameValue);
    if (err) { setNameError(err); return; }
    setSavingName(true);
    try {
      await onUsernameUpdate?.(nameValue);
      setEditingName(false);
      setNameError('');
    } catch {
      setNameError('Failed to save name');
    } finally {
      setSavingName(false);
    }
  };

  const avatarSrc = profile?.avatarUrl
    ? profile.avatarUrl
    : `https://public.readdy.ai/ai/img_res/1240ba7494f642d93170a8283aa87f3e.jpg`;

  return (
    <>
      {/* Cropper Modal */}
      {showCropper && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-red-900/30 rounded-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-zinc-100" style={{ fontFamily: 'Cinzel, serif' }}>Crop Profile Picture</h3>
            <div className="flex flex-col items-center space-y-4">
              <canvas
                ref={canvasRef}
                className="border-2 border-red-900/50 rounded-lg cursor-move"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
              <div className="w-full max-w-xs">
                <label className="block text-sm text-zinc-400 mb-1">Zoom</label>
                <input type="range" min="0.5" max="3" step="0.1" value={cropScale}
                  onChange={(e) => setCropScale(parseFloat(e.target.value))} className="w-full accent-red-500" />
              </div>
              {uploadError && <p className="text-sm text-red-400">{uploadError}</p>}
              <div className="flex gap-3">
                <button type="button" onClick={handleCropCancel} disabled={isUploading}
                  className="px-5 py-2 text-sm text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                  Cancel
                </button>
                <button type="button" onClick={handleCropConfirm} disabled={isUploading}
                  className="px-5 py-2 text-sm text-white bg-red-700 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
                  {isUploading ? 'Uploading...' : 'Confirm'}
                </button>
              </div>
            </div>
            {selectedImage && (
              <img ref={imageRef} src={selectedImage} alt="Selected" className="hidden" onLoad={drawCanvas} />
            )}
          </div>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />

      <div className="bg-zinc-900 border border-red-900/30 rounded-lg p-8">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Clickable Avatar */}
          <div
            className="relative w-40 h-40 flex-shrink-0 group cursor-pointer"
            onClick={handleAvatarClick}
            title="Click to change profile picture"
          >
            <img
              src={avatarSrc}
              alt={profile?.displayName || 'Writer'}
              className="w-full h-full object-cover rounded-full border-4 border-red-900/50 transition-opacity group-hover:opacity-70"
            />
            <div className="absolute inset-0 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-black/60 rounded-full p-3">
                <i className="ri-camera-line text-white text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div>
                {/* Inline display name edit */}
                <div className="flex items-center gap-3 mb-1">
                  {editingName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={nameValue}
                        onChange={(e) => { setNameValue(e.target.value); setNameError(''); }}
                        className="bg-zinc-800 border border-red-900/50 text-zinc-100 text-3xl font-bold rounded px-2 py-1 focus:outline-none focus:border-red-500 w-64"
                        style={{ fontFamily: 'Cinzel, serif' }}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleNameSave();
                          if (e.key === 'Escape') { setEditingName(false); setNameValue(profile.displayName || ''); }
                        }}
                      />
                      <button onClick={handleNameSave} disabled={savingName}
                        className="w-7 h-7 flex items-center justify-center text-green-400 hover:text-green-300 cursor-pointer disabled:opacity-50">
                        <i className="ri-check-line text-lg"></i>
                      </button>
                      <button onClick={() => { setEditingName(false); setNameValue(profile.displayName || ''); setNameError(''); }}
                        className="w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-zinc-200 cursor-pointer">
                        <i className="ri-close-line text-lg"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <h1 className="text-4xl font-bold text-zinc-100" style={{ fontFamily: 'Cinzel, serif' }}>
                        {profile?.displayName || 'Writer'}
                      </h1>
                      <button
                        onClick={() => setEditingName(true)}
                        className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-red-400 transition-colors cursor-pointer"
                        title="Edit name"
                      >
                        <i className="ri-pencil-line text-base"></i>
                      </button>
                    </div>
                  )}
                  <span className="px-3 py-1 bg-red-900/30 border border-red-900/50 text-red-400 text-sm rounded-md whitespace-nowrap" style={{ fontFamily: 'Crimson Text, serif' }}>
                    <i className="ri-quill-pen-line mr-1"></i>Writer
                  </span>
                </div>
                {nameError && <p className="text-xs text-red-400 mb-1">{nameError}</p>}

                <p className="text-zinc-400 text-sm mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                  Joined {formatDate(profile?.joinDate ?? null)}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Notifications */}
                <div className="relative" ref={notifRef}>
                  <button
                    onClick={handleNotificationClick}
                    className="px-5 py-2 bg-zinc-800 hover:bg-zinc-700 border border-red-900/30 text-zinc-100 rounded-md transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer relative"
                    style={{ fontFamily: 'Crimson Text, serif' }}
                  >
                    <i className="ri-notification-3-line"></i>
                    Comments
                    {unreadCount > 0 && (
                      <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-zinc-900">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {showNotifications && (
                    <div className="absolute top-full right-0 mt-2 w-96 max-h-96 overflow-y-auto bg-zinc-800 border border-red-900/30 rounded-lg shadow-2xl z-50">
                      <div className="p-4 border-b border-red-900/30">
                        <h3 className="text-lg font-semibold text-zinc-100" style={{ fontFamily: 'Cinzel, serif' }}>
                          New Comments
                        </h3>
                      </div>
                      {displayedComments.length === 0 ? (
                        <div className="p-6 text-center text-zinc-400" style={{ fontFamily: 'Crimson Text, serif' }}>
                          <i className="ri-chat-3-line text-4xl mb-2 block"></i>
                          No new comments
                        </div>
                      ) : (
                        <div className="divide-y divide-red-900/20">
                          {displayedComments.map((comment) => (
                            <Link
                              key={comment.id}
                              to={`/story/${comment.story_id}`}
                              className="block p-4 transition-colors cursor-pointer"
                              style={{ backgroundColor: 'transparent' }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(63,63,70,0.5)')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                                  <i className="ri-chat-3-fill text-red-500 text-xl"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-zinc-300 mb-1" style={{ fontFamily: 'Crimson Text, serif' }}>
                                    <strong className="text-red-400">{comment.commenter_name}</strong> commented on{' '}
                                    <strong className="text-zinc-100">{comment.story_title}</strong>
                                  </p>
                                  <p className="text-sm text-zinc-400 line-clamp-2 mb-1" style={{ fontFamily: 'Crimson Text, serif' }}>
                                    {comment.content}
                                  </p>
                                  <p className="text-xs text-zinc-500">{formatTimestamp(comment.created_at)}</p>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Link
                  to="/submit"
                  className="px-5 py-2 bg-red-700 hover:bg-red-600 text-white rounded-md transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm font-medium"
                  style={{ fontFamily: 'Cinzel, serif' }}
                >
                  <i className="ri-quill-pen-fill"></i>
                  Write Story
                </Link>
              </div>
            </div>

            <p className="text-zinc-300 leading-relaxed text-lg" style={{ fontFamily: 'Crimson Text, serif' }}>
              {profile?.bio || 'No bio yet.'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
